/**
 * RFC 9457 problem+json error contract (ADR-0006 / ADR-0025). The API returns a stable, machine-
 * readable `code` (+ interpolation `params`); the client translates via the exhaustive
 * {@link errorCodeToI18nKey} map below. The map is `Record<ErrorCode, ...>` so adding a new
 * `ErrorCode` without a translation key is a COMPILE error (DoD requirement).
 *
 * `ErrorCode` enumerates every code the API can emit. Sources (apps/api):
 *   - kernel: `@/common/errors/*` (NotFoundError -> NOT_FOUND, etc.) + status overrides
 *   - feature modules: every `override get code()` in identity/auth/task/economy
 * Keep in sync with the backend; the parity test in `errors.test.ts` + this union guard it.
 */

export const ERROR_CODES = [
  // --- kernel (status overrides + base classes) ---
  'NOT_FOUND',
  'CONFLICT',
  'FORBIDDEN',
  'VALIDATION',
  // request body/params failed class-validator validation (global validation-exception.factory)
  'VALIDATION_FAILED',
  'DATABASE',
  // blob-store (filesystem/GCS) put/read/delete failure — surfaces on photo upload endpoints
  'STORAGE',
  // --- auth ---
  'INVALID_CREDENTIALS',
  'ACCOUNT_LOCKED',
  'INVALID_REFRESH_TOKEN',
  'INVALID_INVITATION',
  'INVALID_PASSWORD_RESET_TOKEN',
  'INVALID_EMAIL_VERIFICATION_TOKEN',
  'REFRESH_TOKEN_NOT_ACTIVE',
  'INVITATION_NOT_PENDING',
  'INVALID_CREDENTIAL_SHAPE',
  'NOT_A_CHILD',
  // a password-reset / email-verification action token is no longer active (used/expired/revoked)
  'ACTION_TOKEN_NOT_ACTIVE',
  // --- identity ---
  'ADMIN_ONLY_PERMISSION',
  'SYSTEM_ROLE_IMMUTABLE',
  'CHILD_ROLE_RESTRICTION',
  'GUARDIAN_CHILD_ROLE',
  'INVALID_USER_SHAPE',
  'EMAIL_ALREADY_IN_USE',
  'ROLE_NOT_IN_HOUSEHOLD',
  'MISSING_CHILD_ROLE',
  'CANNOT_DISABLE_SELF',
  'CANNOT_REMOVE_OWN_ADMIN_ROLE',
  // the action would leave the household with zero active role:manage holders (>=1 admin invariant)
  'LAST_ADMIN',
  // --- compliance / data-rights (M7 35a) ---
  'NOT_HOUSEHOLD_OWNER',
  'HOUSEHOLD_NOT_FOUND',
  // --- task ---
  'TASK_TEMPLATE_NOT_FOUND',
  'TASK_OCCURRENCE_NOT_FOUND',
  'OCCURRENCE_ACCESS_DENIED',
  'INVALID_ASSIGNEES',
  'ILLEGAL_OCCURRENCE_TRANSITION',
  'PHOTO_PROOF_REQUIRED',
  'OCCURRENCE_ACTOR_NOT_ALLOWED',
  'ILLEGAL_TEMPLATE_TRANSITION',
  'INVALID_SCHEDULE_RANGE',
  'CORRUPT_TASK_STATE',
  // back-dated completion (ADR-0040) — the bounded backfill window has passed
  'BACKFILL_WINDOW_EXPIRED',
  // a child acted on a chore for a day other than today (ADR-0083) — past/future is read-only for kids
  'CHORE_ACT_ON_TODAY_ONLY',
  // a child tried to undo an auto-credited chore whose coins already settled on an earlier day (ADR-0141)
  'CHORE_UNDO_WINDOW_EXPIRED',
  // a child tried to "skip" an open-ended anytime mission — a mission has no skip (ADR-0141)
  'MISSION_NOT_SKIPPABLE',
  // a `(template, date)` action targeted a day the schedule no longer makes available (ADR-0142) —
  // the client's cached board is stale; refetch
  'CHORE_NOT_AVAILABLE',
  // `openEnded` (anytime) was set on a non-one-time chore recurrence (ADR-0141)
  'OPEN_ENDED_REQUIRES_ONE_TIME',
  // a supplied photo-proof key belongs to a different occurrence (reused/foreign photo)
  'PHOTO_PROOF_MISMATCH',
  // a chore that requires a photo was completed with NO photo-proof key on the auto-credit path
  'PHOTO_PROOF_MISSING',
  // a guardian tried to back-date a chore completion but lacks the backdate permission
  'CHORE_BACKDATE_NOT_PERMITTED',
  // a "times per period" chore schedule exceeds the allowed count
  'CHORE_TIMES_PER_PERIOD_COUNT_TOO_HIGH',
  // a chore action targeted an archived/inactive template
  'TASK_TEMPLATE_NOT_ACTIVE',
  // a hard-delete was attempted on a chore that has already been used — it can only be archived
  'TASK_TEMPLATE_IN_USE',
  // the supplied photo-proof file type isn't an accepted image type
  'UNSUPPORTED_PHOTO_PROOF_TYPE',
  // the uploaded mission-cover file type isn't an accepted image type (ADR-0141)
  'UNSUPPORTED_TEMPLATE_COVER_TYPE',
  // --- economy ---
  'REWARD_NOT_FOUND',
  'REDEMPTION_NOT_FOUND',
  'HOLD_NOT_FOUND',
  'CATEGORY_NOT_FOUND',
  'CATEGORY_NOT_ASSIGNABLE',
  'CATEGORY_HAS_ACTIVE_HOLD',
  // a hard-delete was attempted on a reward that has redemption history — it can only be archived
  'REWARD_IN_USE',
  'ILLEGAL_CATEGORY_TRANSITION',
  'SAVINGS_GOAL_NOT_FOUND',
  'FAMILY_GOAL_NOT_FOUND',
  'ECONOMY_ACCESS_DENIED',
  'REWARD_NOT_REDEEMABLE',
  'REWARD_NOT_FOR_CHILD',
  'INSUFFICIENT_FUNDS',
  'CURRENCY_MISMATCH',
  'ILLEGAL_REDEMPTION_TRANSITION',
  'REDEMPTION_BLOCKED_BY_HOLD',
  'REDEMPTION_RATE_LIMITED',
  'REDEMPTION_ALREADY_PENDING',
  'INSUFFICIENT_COINS_FOR_REQUEST',
  'REWARD_LOCKED_BY_REQUIREMENT',
  'ILLEGAL_HOLD_TRANSITION',
  'ILLEGAL_REWARD_TRANSITION',
  'SAVINGS_EARMARK_VIOLATION',
  'SAVINGS_GOAL_EXISTS',
  'ILLEGAL_SAVINGS_GOAL_TRANSITION',
  'CORRUPT_ECONOMY_STATE',
  'BONUS_NOT_FOUND',
  'SAVINGS_GOAL_LIMIT_REACHED',
  // a spend would dip into cash already earmarked by the child's savings goals (ADR-0038)
  'CASH_EARMARKED_FOR_SAVINGS',
  'UNSUPPORTED_REWARD_PHOTO_TYPE',
  // --- cosmetics (ADR-0029 wave 33b · catalog management ADR-0096) ---
  'COSMETIC_NOT_FOUND',
  'COSMETIC_ACCESS_DENIED',
  'COSMETIC_NOT_OWNED',
  'COSMETIC_ALREADY_OWNED',
  'COSMETIC_HERO_REQUIRED',
  'COSMETIC_SLUG_TAKEN',
  // a cosmetic can't be archived/deleted while a child still has it equipped
  'COSMETIC_ITEM_IN_USE',
  // --- habit ---
  'HABIT_NOT_FOUND',
  // a hard-delete was attempted on a habit that has check-in history — it can only be archived
  'HABIT_IN_USE',
  'HABIT_ACCESS_DENIED',
  'INVALID_HABIT_CHILD',
  'ILLEGAL_HABIT_TRANSITION',
  'HABIT_NOT_DUE',
  'CORRUPT_HABIT_STATE',
  // back-dated habit check-in (ADR-0040) — the bounded backfill window has passed
  'HABIT_BACKFILL_WINDOW',
  // a guardian tried to back-date a habit check-in but lacks the backdate permission
  'HABIT_BACKDATE_NOT_PERMITTED',
  // a child tried to check a guardian-only habit — only a grown-up may check it in
  'HABIT_GUARDIAN_CHECK_REQUIRED',
  // a habit schedule range is invalid
  'INVALID_HABIT_RANGE',
  // excusing a day was attempted on a flexible (times-per-period) habit, where it has no meaning
  'HABIT_EXCUSE_NOT_APPLICABLE',
  // --- learning (ADR-0027 wave 39a) ---
  'LEARNING_ACCESS_DENIED',
  'LEARNING_NO_ITEM_AVAILABLE',
  'LEARNING_GOAL_NOT_FOUND',
  'LEARNING_TOPIC_NOT_FOUND',
  'LEARNING_PACK_NOT_FOUND',
  'LEARNING_SESSION_NOT_FOUND',
  'LEARNING_LESSON_NOT_FOUND',
  // --- quest ---
  'QUEST_NOT_FOUND',
  'QUEST_NOT_ACTIVE',
  'OBJECTIVE_NOT_FOUND',
  'NOT_A_MILESTONE_OBJECTIVE',
  'NOT_A_TOGETHER_OBJECTIVE',
  'INVALID_QUEST_WINDOW',
  'EMPTY_QUEST_OBJECTIVES',
  'TOO_MANY_QUEST_OBJECTIVES',
  'INVALID_QUEST_COMPLETION_RULE',
  'CORRUPT_QUEST_STATE',
  // --- activity feed ---
  'ACTIVITY_ACCESS_DENIED',
  'ACTIVITY_CHILD_NOT_FOUND',
  // --- play / hero companion (ADR-0095) ---
  'PLAY_ACCESS_DENIED',
  'ARCHETYPE_NOT_FOUND',
  'ARCHETYPE_NOT_AVAILABLE',
  'HERO_NAME_INVALID',
  // a hero is owned-but-locked / not yet unlocked for selection (ADR-0103)
  'HERO_LOCKED',
  // --- play / cross-hero pets + backgrounds (ADR-0108 / ADR-0110 §7) ---
  'PET_NOT_FOUND',
  'PET_NOT_OWNED',
  'BACKGROUND_NOT_FOUND',
  'BACKGROUND_NOT_OWNED',
  // --- identity / avatar ---
  'UNSUPPORTED_AVATAR_PHOTO_TYPE',
  // the avatar photo upload had no `file` part (or a 0-byte one)
  'AVATAR_PHOTO_FILE_REQUIRED',
  // --- notification ---
  'INVALID_PUSH_SUBSCRIPTION',
  // --- compliance / data-rights ---
  'PURGE_DISABLED',
  // --- platform (super-admin console) ---
  'PLATFORM_ADMIN_NOT_FOUND',
  // disabling this platform admin would leave the platform with zero active admins (>=1 admin invariant)
  'LAST_PLATFORM_ADMIN',
  'SYSTEM_EVENT_NOT_FOUND',
  // --- platform / hero-cosmetic bundles (ADR-0097) ---
  'BUNDLE_NO_HERO_ENABLED',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

/** RFC 9457 body the API returns (subset the client consumes). */
export interface ProblemDetails {
  type: string;
  title: string;
  status: number;
  code?: string;
  params?: Record<string, unknown>;
  detail?: string;
  instance?: string;
  traceId?: string;
}

/**
 * Exhaustive code -> i18n key map. `Record<ErrorCode, ...>` makes a missing key a compile error.
 * Keys live in the `errors` namespace (packages/i18n/locales/<locale>/errors.json); the client
 * renders `t(key, params)`.
 */
export const errorCodeToI18nKey: Record<ErrorCode, `errors:${string}`> = {
  NOT_FOUND: 'errors:NOT_FOUND',
  CONFLICT: 'errors:CONFLICT',
  FORBIDDEN: 'errors:FORBIDDEN',
  VALIDATION: 'errors:VALIDATION',
  VALIDATION_FAILED: 'errors:VALIDATION_FAILED',
  DATABASE: 'errors:DATABASE',
  STORAGE: 'errors:STORAGE',
  INVALID_CREDENTIALS: 'errors:INVALID_CREDENTIALS',
  ACCOUNT_LOCKED: 'errors:ACCOUNT_LOCKED',
  INVALID_REFRESH_TOKEN: 'errors:INVALID_REFRESH_TOKEN',
  INVALID_INVITATION: 'errors:INVALID_INVITATION',
  INVALID_PASSWORD_RESET_TOKEN: 'errors:INVALID_PASSWORD_RESET_TOKEN',
  INVALID_EMAIL_VERIFICATION_TOKEN: 'errors:INVALID_EMAIL_VERIFICATION_TOKEN',
  REFRESH_TOKEN_NOT_ACTIVE: 'errors:REFRESH_TOKEN_NOT_ACTIVE',
  INVITATION_NOT_PENDING: 'errors:INVITATION_NOT_PENDING',
  INVALID_CREDENTIAL_SHAPE: 'errors:INVALID_CREDENTIAL_SHAPE',
  NOT_A_CHILD: 'errors:NOT_A_CHILD',
  ACTION_TOKEN_NOT_ACTIVE: 'errors:ACTION_TOKEN_NOT_ACTIVE',
  ADMIN_ONLY_PERMISSION: 'errors:ADMIN_ONLY_PERMISSION',
  SYSTEM_ROLE_IMMUTABLE: 'errors:SYSTEM_ROLE_IMMUTABLE',
  CHILD_ROLE_RESTRICTION: 'errors:CHILD_ROLE_RESTRICTION',
  GUARDIAN_CHILD_ROLE: 'errors:GUARDIAN_CHILD_ROLE',
  INVALID_USER_SHAPE: 'errors:INVALID_USER_SHAPE',
  EMAIL_ALREADY_IN_USE: 'errors:EMAIL_ALREADY_IN_USE',
  ROLE_NOT_IN_HOUSEHOLD: 'errors:ROLE_NOT_IN_HOUSEHOLD',
  MISSING_CHILD_ROLE: 'errors:MISSING_CHILD_ROLE',
  CANNOT_DISABLE_SELF: 'errors:CANNOT_DISABLE_SELF',
  CANNOT_REMOVE_OWN_ADMIN_ROLE: 'errors:CANNOT_REMOVE_OWN_ADMIN_ROLE',
  LAST_ADMIN: 'errors:LAST_ADMIN',
  NOT_HOUSEHOLD_OWNER: 'errors:NOT_HOUSEHOLD_OWNER',
  HOUSEHOLD_NOT_FOUND: 'errors:HOUSEHOLD_NOT_FOUND',
  TASK_TEMPLATE_NOT_FOUND: 'errors:TASK_TEMPLATE_NOT_FOUND',
  TASK_OCCURRENCE_NOT_FOUND: 'errors:TASK_OCCURRENCE_NOT_FOUND',
  OCCURRENCE_ACCESS_DENIED: 'errors:OCCURRENCE_ACCESS_DENIED',
  INVALID_ASSIGNEES: 'errors:INVALID_ASSIGNEES',
  ILLEGAL_OCCURRENCE_TRANSITION: 'errors:ILLEGAL_OCCURRENCE_TRANSITION',
  PHOTO_PROOF_REQUIRED: 'errors:PHOTO_PROOF_REQUIRED',
  OCCURRENCE_ACTOR_NOT_ALLOWED: 'errors:OCCURRENCE_ACTOR_NOT_ALLOWED',
  ILLEGAL_TEMPLATE_TRANSITION: 'errors:ILLEGAL_TEMPLATE_TRANSITION',
  INVALID_SCHEDULE_RANGE: 'errors:INVALID_SCHEDULE_RANGE',
  CORRUPT_TASK_STATE: 'errors:CORRUPT_TASK_STATE',
  BACKFILL_WINDOW_EXPIRED: 'errors:BACKFILL_WINDOW_EXPIRED',
  CHORE_ACT_ON_TODAY_ONLY: 'errors:CHORE_ACT_ON_TODAY_ONLY',
  CHORE_UNDO_WINDOW_EXPIRED: 'errors:CHORE_UNDO_WINDOW_EXPIRED',
  MISSION_NOT_SKIPPABLE: 'errors:MISSION_NOT_SKIPPABLE',
  CHORE_NOT_AVAILABLE: 'errors:CHORE_NOT_AVAILABLE',
  OPEN_ENDED_REQUIRES_ONE_TIME: 'errors:OPEN_ENDED_REQUIRES_ONE_TIME',
  PHOTO_PROOF_MISMATCH: 'errors:PHOTO_PROOF_MISMATCH',
  PHOTO_PROOF_MISSING: 'errors:PHOTO_PROOF_MISSING',
  CHORE_BACKDATE_NOT_PERMITTED: 'errors:CHORE_BACKDATE_NOT_PERMITTED',
  CHORE_TIMES_PER_PERIOD_COUNT_TOO_HIGH: 'errors:CHORE_TIMES_PER_PERIOD_COUNT_TOO_HIGH',
  TASK_TEMPLATE_NOT_ACTIVE: 'errors:TASK_TEMPLATE_NOT_ACTIVE',
  TASK_TEMPLATE_IN_USE: 'errors:TASK_TEMPLATE_IN_USE',
  UNSUPPORTED_PHOTO_PROOF_TYPE: 'errors:UNSUPPORTED_PHOTO_PROOF_TYPE',
  UNSUPPORTED_TEMPLATE_COVER_TYPE: 'errors:UNSUPPORTED_TEMPLATE_COVER_TYPE',
  REWARD_NOT_FOUND: 'errors:REWARD_NOT_FOUND',
  REDEMPTION_NOT_FOUND: 'errors:REDEMPTION_NOT_FOUND',
  HOLD_NOT_FOUND: 'errors:HOLD_NOT_FOUND',
  CATEGORY_NOT_FOUND: 'errors:CATEGORY_NOT_FOUND',
  CATEGORY_NOT_ASSIGNABLE: 'errors:CATEGORY_NOT_ASSIGNABLE',
  CATEGORY_HAS_ACTIVE_HOLD: 'errors:CATEGORY_HAS_ACTIVE_HOLD',
  REWARD_IN_USE: 'errors:REWARD_IN_USE',
  ILLEGAL_CATEGORY_TRANSITION: 'errors:ILLEGAL_CATEGORY_TRANSITION',
  SAVINGS_GOAL_NOT_FOUND: 'errors:SAVINGS_GOAL_NOT_FOUND',
  FAMILY_GOAL_NOT_FOUND: 'errors:FAMILY_GOAL_NOT_FOUND',
  ECONOMY_ACCESS_DENIED: 'errors:ECONOMY_ACCESS_DENIED',
  REWARD_NOT_REDEEMABLE: 'errors:REWARD_NOT_REDEEMABLE',
  REWARD_NOT_FOR_CHILD: 'errors:REWARD_NOT_FOR_CHILD',
  INSUFFICIENT_FUNDS: 'errors:INSUFFICIENT_FUNDS',
  CURRENCY_MISMATCH: 'errors:CURRENCY_MISMATCH',
  ILLEGAL_REDEMPTION_TRANSITION: 'errors:ILLEGAL_REDEMPTION_TRANSITION',
  REDEMPTION_BLOCKED_BY_HOLD: 'errors:REDEMPTION_BLOCKED_BY_HOLD',
  REDEMPTION_RATE_LIMITED: 'errors:REDEMPTION_RATE_LIMITED',
  REDEMPTION_ALREADY_PENDING: 'errors:REDEMPTION_ALREADY_PENDING',
  INSUFFICIENT_COINS_FOR_REQUEST: 'errors:INSUFFICIENT_COINS_FOR_REQUEST',
  REWARD_LOCKED_BY_REQUIREMENT: 'errors:REWARD_LOCKED_BY_REQUIREMENT',
  ILLEGAL_HOLD_TRANSITION: 'errors:ILLEGAL_HOLD_TRANSITION',
  ILLEGAL_REWARD_TRANSITION: 'errors:ILLEGAL_REWARD_TRANSITION',
  SAVINGS_EARMARK_VIOLATION: 'errors:SAVINGS_EARMARK_VIOLATION',
  SAVINGS_GOAL_EXISTS: 'errors:SAVINGS_GOAL_EXISTS',
  ILLEGAL_SAVINGS_GOAL_TRANSITION: 'errors:ILLEGAL_SAVINGS_GOAL_TRANSITION',
  CORRUPT_ECONOMY_STATE: 'errors:CORRUPT_ECONOMY_STATE',
  BONUS_NOT_FOUND: 'errors:BONUS_NOT_FOUND',
  SAVINGS_GOAL_LIMIT_REACHED: 'errors:SAVINGS_GOAL_LIMIT_REACHED',
  CASH_EARMARKED_FOR_SAVINGS: 'errors:CASH_EARMARKED_FOR_SAVINGS',
  UNSUPPORTED_REWARD_PHOTO_TYPE: 'errors:UNSUPPORTED_REWARD_PHOTO_TYPE',
  COSMETIC_NOT_FOUND: 'errors:COSMETIC_NOT_FOUND',
  COSMETIC_ACCESS_DENIED: 'errors:COSMETIC_ACCESS_DENIED',
  COSMETIC_NOT_OWNED: 'errors:COSMETIC_NOT_OWNED',
  COSMETIC_ALREADY_OWNED: 'errors:COSMETIC_ALREADY_OWNED',
  COSMETIC_HERO_REQUIRED: 'errors:COSMETIC_HERO_REQUIRED',
  COSMETIC_SLUG_TAKEN: 'errors:COSMETIC_SLUG_TAKEN',
  COSMETIC_ITEM_IN_USE: 'errors:COSMETIC_ITEM_IN_USE',
  HABIT_NOT_FOUND: 'errors:HABIT_NOT_FOUND',
  HABIT_IN_USE: 'errors:HABIT_IN_USE',
  HABIT_ACCESS_DENIED: 'errors:HABIT_ACCESS_DENIED',
  INVALID_HABIT_CHILD: 'errors:INVALID_HABIT_CHILD',
  ILLEGAL_HABIT_TRANSITION: 'errors:ILLEGAL_HABIT_TRANSITION',
  HABIT_NOT_DUE: 'errors:HABIT_NOT_DUE',
  CORRUPT_HABIT_STATE: 'errors:CORRUPT_HABIT_STATE',
  HABIT_BACKFILL_WINDOW: 'errors:HABIT_BACKFILL_WINDOW',
  HABIT_BACKDATE_NOT_PERMITTED: 'errors:HABIT_BACKDATE_NOT_PERMITTED',
  HABIT_GUARDIAN_CHECK_REQUIRED: 'errors:HABIT_GUARDIAN_CHECK_REQUIRED',
  INVALID_HABIT_RANGE: 'errors:INVALID_HABIT_RANGE',
  HABIT_EXCUSE_NOT_APPLICABLE: 'errors:HABIT_EXCUSE_NOT_APPLICABLE',
  LEARNING_ACCESS_DENIED: 'errors:LEARNING_ACCESS_DENIED',
  LEARNING_NO_ITEM_AVAILABLE: 'errors:LEARNING_NO_ITEM_AVAILABLE',
  LEARNING_GOAL_NOT_FOUND: 'errors:LEARNING_GOAL_NOT_FOUND',
  LEARNING_TOPIC_NOT_FOUND: 'errors:LEARNING_TOPIC_NOT_FOUND',
  LEARNING_PACK_NOT_FOUND: 'errors:LEARNING_PACK_NOT_FOUND',
  LEARNING_SESSION_NOT_FOUND: 'errors:LEARNING_SESSION_NOT_FOUND',
  LEARNING_LESSON_NOT_FOUND: 'errors:LEARNING_LESSON_NOT_FOUND',
  QUEST_NOT_FOUND: 'errors:QUEST_NOT_FOUND',
  QUEST_NOT_ACTIVE: 'errors:QUEST_NOT_ACTIVE',
  OBJECTIVE_NOT_FOUND: 'errors:OBJECTIVE_NOT_FOUND',
  NOT_A_MILESTONE_OBJECTIVE: 'errors:NOT_A_MILESTONE_OBJECTIVE',
  NOT_A_TOGETHER_OBJECTIVE: 'errors:NOT_A_TOGETHER_OBJECTIVE',
  INVALID_QUEST_WINDOW: 'errors:INVALID_QUEST_WINDOW',
  EMPTY_QUEST_OBJECTIVES: 'errors:EMPTY_QUEST_OBJECTIVES',
  TOO_MANY_QUEST_OBJECTIVES: 'errors:TOO_MANY_QUEST_OBJECTIVES',
  INVALID_QUEST_COMPLETION_RULE: 'errors:INVALID_QUEST_COMPLETION_RULE',
  CORRUPT_QUEST_STATE: 'errors:CORRUPT_QUEST_STATE',
  ACTIVITY_ACCESS_DENIED: 'errors:ACTIVITY_ACCESS_DENIED',
  ACTIVITY_CHILD_NOT_FOUND: 'errors:ACTIVITY_CHILD_NOT_FOUND',
  PLAY_ACCESS_DENIED: 'errors:PLAY_ACCESS_DENIED',
  ARCHETYPE_NOT_FOUND: 'errors:ARCHETYPE_NOT_FOUND',
  ARCHETYPE_NOT_AVAILABLE: 'errors:ARCHETYPE_NOT_AVAILABLE',
  HERO_NAME_INVALID: 'errors:HERO_NAME_INVALID',
  HERO_LOCKED: 'errors:HERO_LOCKED',
  PET_NOT_FOUND: 'errors:PET_NOT_FOUND',
  PET_NOT_OWNED: 'errors:PET_NOT_OWNED',
  BACKGROUND_NOT_FOUND: 'errors:BACKGROUND_NOT_FOUND',
  BACKGROUND_NOT_OWNED: 'errors:BACKGROUND_NOT_OWNED',
  UNSUPPORTED_AVATAR_PHOTO_TYPE: 'errors:UNSUPPORTED_AVATAR_PHOTO_TYPE',
  AVATAR_PHOTO_FILE_REQUIRED: 'errors:AVATAR_PHOTO_FILE_REQUIRED',
  INVALID_PUSH_SUBSCRIPTION: 'errors:INVALID_PUSH_SUBSCRIPTION',
  PURGE_DISABLED: 'errors:PURGE_DISABLED',
  PLATFORM_ADMIN_NOT_FOUND: 'errors:PLATFORM_ADMIN_NOT_FOUND',
  LAST_PLATFORM_ADMIN: 'errors:LAST_PLATFORM_ADMIN',
  SYSTEM_EVENT_NOT_FOUND: 'errors:SYSTEM_EVENT_NOT_FOUND',
  BUNDLE_NO_HERO_ENABLED: 'errors:BUNDLE_NO_HERO_ENABLED',
};

const ERROR_CODE_SET = new Set<string>(ERROR_CODES);

/** Narrow an arbitrary string to a known `ErrorCode`. */
export function isErrorCode(code: string | undefined): code is ErrorCode {
  return code !== undefined && ERROR_CODE_SET.has(code);
}

/** Extract a `ProblemDetails` from an unknown thrown value (axios error or raw body). */
export function toProblemDetails(error: unknown): ProblemDetails | null {
  if (error && typeof error === 'object') {
    const maybeAxios = error as { response?: { data?: unknown }; isAxiosError?: boolean };
    const data = maybeAxios.response?.data ?? error;
    if (data && typeof data === 'object' && 'status' in data && 'title' in data) {
      return data as ProblemDetails;
    }
  }
  return null;
}

/** The i18n key + interpolation params an error resolves to — the shape every error-display surface uses. */
export type ResolvedError = {
  key: string;
  params: Record<string, unknown>;
};

/** Resolve the i18n key (+ params) for an unknown error. Falls back to `errors.unknown`/`network`. */
export function resolveErrorMessage(error: unknown): ResolvedError {
  const problem = toProblemDetails(error);
  if (problem && isErrorCode(problem.code)) {
    return { key: errorCodeToI18nKey[problem.code], params: problem.params ?? {} };
  }
  // No response at all => likely a network/connectivity error.
  if (
    error &&
    typeof error === 'object' &&
    'isAxiosError' in error &&
    !(error as { response?: unknown }).response
  ) {
    return { key: 'errors:network', params: {} };
  }
  return { key: 'errors:unknown', params: {} };
}
